import Application from 'models/core/application/Model.Application';
import MediaFile from 'models/core/mediaFile/Model.MediaFile';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Model from 'models/Model';
import RefundConfigTemplate from 'models/productManagement/refundConfigTemplate/Model.RefundConfigTemplate';
import ContractCancellation from '../../sales/contractCancellation/Model.ContractCancellation';
import ContractRefundComment from '../contractRefundComment/Model.ContractRefundComment';
import ContractRefundDealerPaymentControl from '../contractRefundDealerPaymentControl/Model.ContractRefundDealerPaymentControl';
import ContractRefundDocument from '../contractRefundDocument/Model.ContractRefundDocument';
import ContractRefundPayee from '../contractRefundPayee/Model.ContractRefundPayee';
import contractRefundFindApi from './find/Api.ContractRefund.Find';
import contractRefundListApi from './list/Api.ContractRefund.List';

export default class ContractRefund
  extends Model<Model.IContractRefund>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.IContractRefund>
{
  id: number;
  statusCode: string;
  readonly jobId: string;

  refundDateAtLocal?: string;
  createdAtUtc: string;
  modifiedAtUtc: string;

  isLoyaltyPremiumApplied: boolean;
  newAppNumber?: string;
  areClaimDeductionsApplied: boolean;

  refundConfigTemplateId: number;
  refundConfigTemplate: RefundConfigTemplate;
  refundConfigTemplateJson: RefundConfigTemplate;

  contractCancellationId: number;
  contractCancellation: ContractCancellation;

  dealerId?: number;
  dealer?: Dealer;

  createdById?: number;
  createdBy?: User;

  modifiedById?: number;
  modifiedBy?: User;

  cancellationNote?: string;
  cancelledAtUtc?: string;
  cancelledById?: number;
  cancelledBy?: User;

  applicationId?: number;
  application?: Application;

  contractRefundPayees?: ContractRefundPayee[];
  contractRefundComments?: ContractRefundComment[];
  contractRefundDocuments?: ContractRefundDocument[];
  dealerPaymentControls?: ContractRefundDealerPaymentControl[];
  documentationAggregateId?: number;
  documentationAggregate?: MediaFile;
  documentLanguageIsoCode?: Locale.Languages;

  riskFactor: number;
  retainFactor: number;

  totalDeductions?: number;
  totalDeductionsTax?: number;
  readonly totalDeductionsTaxAmount?: number;
  readonly totalDeductionsWithTaxes: number;

  // =============================
  // Paid Claim Amounts on Contract
  // =============================

  sumOfClaimAmountsPaidOnContract?: number;
  sumOfClaimGstHstAmountsPaidOnContract?: number;
  sumOfClaimProvincialSalesTaxAmountsPaidOnContract?: number;
  readonly totalSumOfClaimAmountsWithTaxesPaidOnContract?: number;
  readonly totalClaimDeductions?: number;

  readonly refundFactor: number;

  // =============================
  // Terms
  // =============================

  termInDays: number;
  usedTermInDays: number;
  readonly remainingTermInDays: number;

  usedTermInDaysPercentage: number;
  readonly remainingTermInDaysPercentage: number;

  termInKms?: number;
  usedOdometerReading?: number;
  readonly remainingOdometerReading?: number;

  usedOdometerReadingPercentage?: number;
  readonly remainingOdometerReadingPercentage?: number;

  // =============================
  // Dealer Cost Prices
  // =============================

  retainedDealerCostAmt: number;
  retainedDealerCostTaxAmt: number;
  readonly retainedDealerCostTotal: number;
  readonly retainedDealerCostTotalPlusDeductions: number;

  refundedDealerCostTotal: number;
  refundedDealerCostTaxAmt: number;
  readonly refundedDealerCostAmt: number;
  readonly refundedDealerCostTotalLessDeductions: number;

  // =============================
  // Dealer Profit Prices
  // =============================

  unprotectedDealerProfitTotal?: number;
  unprotectedDealerProfitTaxAmt?: number;
  readonly unprotectedDealerProfitAmt?: number;

  retainedDealerProfitTotal: number;
  retainedDealerProfitTaxAmt: number;
  readonly retainedDealerProfitAmt: number;

  refundedDealerProfitTotal: number;
  refundedDealerProfitTaxAmt: number;
  readonly refundedDealerProfitAmt: number;
  isRefundedDealerProfitTotalExcluded?: boolean;

  // =============================
  // ASC Dealer Profit Prices
  // =============================

  retainedASCDealerProfitTotal: number;
  retainedASCDealerProfitTaxAmt: number;
  readonly retainedASCDealerProfitAmt: number;

  refundedASCDealerProfitTotal: number;
  refundedASCDealerProfitTaxAmt: number;
  readonly refundedASCDealerProfitAmt: number;
  isRefundedASCDealerProfitTotalExcluded?: boolean;

  // =============================
  // Distributions
  // =============================

  pricingDistributions: Utils.ContractRefundDistribution;
  distributorDistributions: Utils.ContractRefundDistribution;
  subfees: Utils.ContractRefundSubfee[];
  enhancements: Utils.ContractRefundEnhancement[];

  // =============================
  // Sale Values
  // =============================

  readonly contractExists: boolean;

  readonly dealerCostTaxAtSale: number;
  readonly dealerProfitTaxAtSale: number;

  readonly dealerCostValueAtSale: number;
  readonly dealerProfitValueAtSale: number;

  readonly retailSalePriceValueAtSale: number;
  readonly retailSalePriceTaxValueAtSale: number;

  // =============================
  // Totals
  // =============================

  readonly totalRetainedDealerAmt?: number;
  readonly totalRetainedDealerTaxAmt?: number;
  readonly totalRetainedDealerSubtotal?: number;

  readonly totalRefundedDealerAmt?: number;
  readonly totalRefundedDealerTaxAmt?: number;
  readonly totalRefundedDealerSubtotal?: number;

  readonly retainedDealerTotal?: number;
  readonly refundedDealerTotal?: number;

  readonly totalRefundDeductions?: number;
  readonly totalToBeRefunded?: number;

  constructor(
    data: Partial<Model.IContractRefund> | Utils.FormData<Model.IContractRefund>,
    language?: Locale.Languages
  ) {
    super(
      {
        refundConfigTemplate: RefundConfigTemplate,
        refundConfigTemplateJson: RefundConfigTemplate,
        contractCancellation: ContractCancellation,
        dealer: Dealer,
        createdBy: User,
        modifiedBy: User,
        cancelledBy: User,
        application: Application,
        contractRefundPayees: [ContractRefundPayee],
        contractRefundComments: [ContractRefundComment],
        contractRefundDocuments: [ContractRefundDocument],
        dealerPaymentControls: [ContractRefundDealerPaymentControl],
        documentationAggregate: MediaFile,
      },
      language
    );

    this.update(data);
  }

  get displayInfo(): Model.DisplayInfo {
    return {
      id: this.id,
      title: this.id.toString(),
    };
  }

  static async list(body?: Api.Sales.ContractRefund.List.IRequest) {
    const { payload, ...rest } = await contractRefundListApi(body);

    return {
      ...rest,
      payload: payload && {
        data: payload.data?.map((it) => new ContractRefund(it)),
        count: payload.count,
      },
    };
  }

  static async find(params: { id: number }) {
    const { payload, ...rest } = await contractRefundFindApi(params);

    return {
      ...rest,
      payload: new ContractRefund(payload),
    };
  }
}
